// components/admin/ProductsManagerModal.js - Excel-like batch editing
import React, { useState, useEffect, useRef } from "react"
import {
  FiX,
  FiEdit,
  FiTrash2,
  FiEye,
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiSearch,
  FiTag,
  FiPackage,
  FiLayers,
  FiCode,
  FiDollarSign,
  FiSave,
} from "react-icons/fi"
import { updateProduct } from "../../api"
import { FaUndo } from "react-icons/fa"

const ProductsManagerModal = ({
  products,
  context,
  onClose,
  onProductEdit,
  onProductDelete,
  onProductView,
  onProductsUpdated, // NUOVA CALLBACK per notificare aggiornamenti
}) => {
  const [filteredProducts, setFilteredProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" })

  // NUOVO: State per modalità editing
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedProducts, setEditedProducts] = useState({})
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)

  // NUOVO: Refs per gestire focus nelle celle
  const cellRefs = useRef({})

  // Initialize filtered products
  useEffect(() => {
    setFilteredProducts(products)
    initializeEditedProducts(products)
  }, [products])

  const initializeEditedProducts = (productsList) => {
    const edited = {}
    productsList.forEach((product) => {
      edited[product.code] = {
        ...product, // PRESERVA TUTTO il prodotto originale
        // Assicurati che tutti i campi necessari esistano per l'editing
        name: getDisplayName(product),
        onpagecode: product.onpage_code || product.onpagecode || "", // Entrambe le varianti
        price: product.price || 0,
        tag: product.tag || "",
      }
    })
    setEditedProducts(edited)
  }
  // NUOVO: Ottieni il nome da visualizzare (lang.it se presente)
  const getDisplayName = (product) => {
    if (product.lang && product.lang.it) {
      return product.lang.it
    }
    return product.name || ""
  }

  // Handle search
  const handleSearch = (e) => {
    const term = e.target.value
    setSearchTerm(term)

    if (!term.trim()) {
      setFilteredProducts(products)
      return
    }

    const searchResults = products.filter(
      (product) =>
        getDisplayName(product).toLowerCase().includes(term.toLowerCase()) ||
        product.code.toLowerCase().includes(term.toLowerCase()) ||
        (product.onpage_code &&
          product.onpage_code.toLowerCase().includes(term.toLowerCase())) ||
        (product.projectCode &&
          product.projectCode.toLowerCase().includes(term.toLowerCase())) ||
        (product.slug &&
          product.slug.toLowerCase().includes(term.toLowerCase())) ||
        (product.tag && product.tag.toLowerCase().includes(term.toLowerCase()))
    )

    setFilteredProducts(searchResults)
  }

  // Handle sorting
  const requestSort = (key) => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })

    const sortedProducts = [...filteredProducts].sort((a, b) => {
      // Per i prodotti in modalità edit, usa i valori editati
      const aValue = isEditMode
        ? editedProducts[a.code]?.[key] || a[key]
        : a[key]
      const bValue = isEditMode
        ? editedProducts[b.code]?.[key] || b[key]
        : b[key]

      // Handle null values
      if (!aValue && !bValue) return 0
      if (!aValue) return 1
      if (!bValue) return -1

      // Handle string comparison
      if (typeof aValue === "string") {
        return direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      // Handle number comparison
      return direction === "asc" ? aValue - bValue : bValue - aValue
    })

    setFilteredProducts(sortedProducts)
  }

  // NUOVO: Gestione delle modifiche nelle celle
  const handleCellChange = (productCode, field, value) => {
    setEditedProducts((prev) => {
      const updated = {
        ...prev,
        [productCode]: {
          ...prev[productCode],
          [field]: value,
        },
      }
      return updated
    })

    setHasChanges(true)
  }

  // NUOVO: Attiva/disattiva modalità editing
  const toggleEditMode = () => {
    if (isEditMode && hasChanges) {
      if (!window.confirm("Hai modifiche non salvate. Vuoi scartarle?")) {
        return
      }
    }

    setIsEditMode(!isEditMode)
    if (!isEditMode) {
      // Entrando in modalità edit, reinizializza i dati
      initializeEditedProducts(filteredProducts)
      setHasChanges(false)
    }
  }

  // NUOVO: Salva tutte le modifiche con validazione robusta
  const handleSaveAllChanges = async () => {
    setSaving(true)
    const results = { success: [], failed: [] }

    try {
      // Identifica i prodotti che sono stati modificati
      const modifiedProducts = Object.keys(editedProducts).filter((code) => {
        const original = products.find((p) => p.code === code)
        const edited = editedProducts[code]

        return (
          original &&
          (getDisplayName(original) !== edited.name ||
            (original.onpage_code || original.projectCode || "") !==
              edited.onpage_code ||
            (original.price || 0) !== edited.price ||
            (original.tag || "") !== edited.tag)
        )
      })

      if (modifiedProducts.length === 0) {
        alert("Nessuna modifica da salvare.")
        setSaving(false)
        return
      }

      // Conferma prima di procedere
      if (!window.confirm(`Stai per aggiornare ${modifiedProducts.length} prodotti. Continuare?`)) {
        setSaving(false)
        return
      }

      // Salva ogni prodotto modificato
      for (const code of modifiedProducts) {
        try {
          const editedProduct = editedProducts[code]
          const originalProduct = products.find((p) => p.code === code)

          const updatedProduct = {
            // MANTIENI TUTTO dal prodotto originale
            ...originalProduct,
            // Applica SOLO le modifiche specifiche
            name: editedProduct.name,
            price: editedProduct.price,
            tag: editedProduct.tag,
            // PRESERVA SEMPRE onpage_code
            onpage_code:
              editedProduct.onpagecode ||
              originalProduct.onpage_code ||
              originalProduct.onpagecode ||
              "",
            // Se c'è lang.it, aggiornalo mantenendo tutti gli altri campi lang
            ...(originalProduct.lang && {
              lang: {
                ...originalProduct.lang,
                ...(originalProduct.lang.it && { it: editedProduct.name }),
              },
            }),
            // PRESERVA ESPLICITAMENTE tutti gli altri campi critici
            customViews: originalProduct.customViews || [],
            materialLinks: originalProduct.materialLinks || [],
            materials: originalProduct.materials || {},
            typeLinks: originalProduct.typeLinks || [],
            slug: originalProduct.slug || "",
            projectCode: originalProduct.projectCode || "",
          }

          await updateProduct(updatedProduct)
          results.success.push(code)
        } catch (error) {
          console.error("Error updating product", code, error)
          results.failed.push({ code, error: error.message })
        }
      }

      // Notifica risultati dettagliati
      if (results.failed.length === 0) {
        alert(`✅ Aggiornati con successo ${results.success.length} prodotti.`)

        // Solo se TUTTO è andato a buon fine, refresh e esci da edit mode
        if (onProductsUpdated) {
          await onProductsUpdated()
        }
        setIsEditMode(false)
        setHasChanges(false)
      } else {
        // Ci sono stati errori - NON fare refresh automatico
        const failedCodes = results.failed.map(f => f.code).join(", ")
        alert(
          `⚠️ Aggiornati ${results.success.length} prodotti.\n` +
          `❌ ${results.failed.length} errori per: ${failedCodes}\n\n` +
          `I prodotti falliti NON sono stati modificati. Puoi riprovare o verificare manualmente.`
        )

        // Rimuovi dai prodotti editati quelli che hanno avuto successo
        // così l'utente può riprovare solo quelli falliti
        setEditedProducts((prev) => {
          const updated = { ...prev }
          results.success.forEach((code) => {
            const original = products.find((p) => p.code === code)
            if (original) {
              updated[code] = {
                ...original,
                name: getDisplayName(original),
                onpagecode: original.onpage_code || original.onpagecode || "",
                price: original.price || 0,
                tag: original.tag || "",
              }
            }
          })
          return updated
        })

        // NON uscire da edit mode - lascia l'utente correggere
        // NON chiamare onProductsUpdated - evita refresh parziale
      }
    } catch (error) {
      console.error("Error during batch save:", error)
      alert("❌ Errore critico durante il salvataggio batch. Nessuna modifica applicata.")
    } finally {
      setSaving(false)
    }
  }

  // NUOVO: Annulla le modifiche
  const handleCancelChanges = () => {
    if (hasChanges) {
      if (!window.confirm("Sei sicuro di voler scartare tutte le modifiche?")) {
        return
      }
    }

    initializeEditedProducts(filteredProducts)
    setHasChanges(false)
    setIsEditMode(false)
  }

  // Export products data as CSV
  const handleExportCSV = () => {
    // MODIFICATO: Usa i dati editati se in modalità edit
    const dataToExport = isEditMode
      ? filteredProducts.map((p) => editedProducts[p.code] || p)
      : filteredProducts

    // Create header row - AGGIORNATO con nuovo campo
    const headers = ["name", "code", "onpage_code", "price", "tag"]
    const headerRow = headers.join(",")

    // Create data rows
    const dataRows = dataToExport.map((product) => {
      return headers
        .map((header) => {
          // Wrap values in quotes to handle commas in strings
          const value =
            product[header] !== undefined && product[header] !== null
              ? String(product[header]).replace(/"/g, '""') // Escape quotes
              : ""
          return `"${value}"`
        })
        .join(",")
    })

    // Combine header and data rows
    const csvContent = [headerRow, ...dataRows].join("\n")

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `products_${context.category}_${context.family}.csv`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) {
      return null
    }
    return sortConfig.direction === "asc" ? "↑" : "↓"
  }

  // NUOVO: Componente per celle editabili
  const EditableCell = ({ product, field, type = "text", options = null }) => {
    const isEditing = isEditMode
    const value = editedProducts[product.code]?.[field] || ""
    const cellId = `${product.code}-${field}`

    if (!isEditing) {
      // Modalità sola lettura
      if (field === "name") {
        return getDisplayName(product)
      }
      if (field === "onpage_code") {
        return product.onpage_code || product.projectCode || ""
      }
      if (field === "price") {
        return product.price ? `€${product.price.toLocaleString()}` : "-"
      }
      if (field === "tag") {
        return product.tag ? (
          <span className="ay-px-2 ay-py-1 ay-text-xs ay-bg-blue-100 ay-text-blue-800 ay-rounded">
            {product.tag}
          </span>
        ) : (
          "-"
        )
      }
      return product[field] || "-"
    }

    // Modalità editing
    if (type === "select" && options) {
      return (
        <select
          value={value}
          onChange={(e) =>
            handleCellChange(product.code, field, e.target.value)
          }
          className="ay-w-full ay-px-2 ay-py-1 ay-border ay-border-gray-300 ay-rounded ay-text-sm ay-focus:ay-outline-none ay-focus:ay-ring-1 ay-focus:ay-ring-blue-500"
        >
          <option value="">None</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )
    }

    if (type === "number") {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) =>
            handleCellChange(
              product.code,
              field,
              parseFloat(e.target.value) || 0
            )
          }
          className="ay-w-full ay-px-2 ay-py-1 ay-border ay-border-gray-300 ay-rounded ay-text-sm ay-focus:ay-outline-none ay-focus:ay-ring-1 ay-focus:ay-ring-blue-500"
          min="0"
          step="0.01"
        />
      )
    }

    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleCellChange(product.code, field, e.target.value)}
        className="ay-w-full ay-px-2 ay-py-1 ay-border ay-border-gray-300 ay-rounded ay-text-sm ay-focus:ay-outline-none ay-focus:ay-ring-1 ay-focus:ay-ring-blue-500"
        ref={(el) => (cellRefs.current[cellId] = el)}
      />
    )
  }

  // Tag options for dropdown
  const tagOptions = ["", "bestseller", "new", "sale", "premium"]

  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50 ay-overflow-y-auto ay-py-10">
      <div className="ay-bg-white ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-7xl ay-max-h-full ay-flex ay-flex-col ay-mx-4">
        <div className="ay-flex ay-justify-between ay-items-center ay-p-4 ay-border-b ay-border-gray-200">
          <h2 className="ay-text-xl ay-font-bold ay-flex ay-items-center ay-gap-2">
            <FiLayers className="ay-text-blue-500" />
            Products Manager: {context.category} / {context.family}
            {isEditMode && (
              <span className="ay-ml-2 ay-px-2 ay-py-1 ay-bg-orange-100 ay-text-orange-800 ay-text-sm ay-rounded">
                EDIT MODE
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="ay-p-2 ay-text-gray-500 ay-hover:ay-text-gray-700 ay-rounded-full ay-hover:ay-bg-gray-100"
          >
            <FiX />
          </button>
        </div>

        {/* Toolbar - MODIFICATO */}
        <div className="ay-flex ay-items-center ay-justify-between ay-p-4 ay-bg-gray-50">
          <div className="ay-flex ay-items-center ay-gap-2 ay-w-1/2">
            <div className="ay-relative ay-flex-grow">
              <div className="ay-absolute ay-inset-y-0 ay-left-0 ay-pl-3 ay-flex ay-items-center ay-pointer-events-none">
                <FiSearch className="ay-text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search products..."
                className="ay-block ay-w-full ay-pl-10 ay-pr-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                disabled={saving}
              />
            </div>
            <button
              onClick={() => setFilteredProducts(products)}
              className="ay-p-2 ay-bg-gray-200 ay-text-gray-700 ay-rounded ay-hover:ay-bg-gray-300 ay-flex ay-items-center ay-gap-1"
              title="Reset filters"
              disabled={saving}
            >
              <FiRefreshCw />
            </button>
          </div>

          <div className="ay-flex ay-items-center ay-gap-2">
            <span className="ay-text-sm ay-text-gray-500">
              {filteredProducts.length} of {products.length} products
            </span>

            {/* NUOVO: Controlli per modalità editing */}
            {!isEditMode ? (
              <>
                <button
                  onClick={toggleEditMode}
                  className="ay-px-3 ay-py-2 ay-bg-orange-500 ay-text-white ay-rounded ay-hover:ay-bg-orange-600 ay-flex ay-items-center ay-gap-1"
                >
                  <FiEdit /> Edit Mode
                </button>
                <button
                  onClick={handleExportCSV}
                  className="ay-px-3 ay-py-2 ay-bg-green-500 ay-text-white ay-rounded ay-hover:ay-bg-green-600 ay-flex ay-items-center ay-gap-1"
                >
                  <FiDownload /> Export CSV
                </button>
              </>
            ) : (
              <>
                {hasChanges && (
                  <span className="ay-text-sm ay-text-orange-600 ay-font-medium">
                    Changes pending
                  </span>
                )}
                <button
                  onClick={handleCancelChanges}
                  className="ay-px-3 ay-py-2 ay-bg-gray-500 ay-text-white ay-rounded ay-hover:ay-bg-gray-600 ay-flex ay-items-center ay-gap-1"
                  disabled={saving}
                >
                  <FaUndo /> Cancel
                </button>
                <button
                  onClick={handleSaveAllChanges}
                  className="ay-px-3 ay-py-2 ay-bg-green-600 ay-text-white ay-rounded ay-hover:ay-bg-green-700 ay-flex ay-items-center ay-gap-1"
                  disabled={saving || !hasChanges}
                >
                  <FiSave /> {saving ? "Saving..." : "Save All"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Products Table - MODIFICATO */}
        <div className="ay-overflow-x-auto ay-flex-grow">
          <table className="ay-min-w-full ay-divide-y ay-divide-gray-200">
            <thead className="ay-bg-gray-50 ay-sticky ay-top-0">
              <tr>
                <th
                  className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider ay-cursor-pointer ay-hover:ay-bg-gray-100"
                  onClick={() => requestSort("name")}
                >
                  <div className="ay-flex ay-items-center ay-gap-1">
                    Name {getSortDirectionIndicator("name")}
                  </div>
                </th>
                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                  <div className="ay-flex ay-items-center ay-gap-1">
                    <FiCode className="ay-text-gray-400" />
                    Code
                  </div>
                </th>
                <th
                  className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider ay-cursor-pointer ay-hover:ay-bg-gray-100"
                  onClick={() => requestSort("onpage_code")}
                >
                  <div className="ay-flex ay-items-center ay-gap-1">
                    <FiPackage className="ay-text-gray-400" />
                    Onpage Code {getSortDirectionIndicator("onpage_code")}
                  </div>
                </th>
                <th
                  className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider ay-cursor-pointer ay-hover:ay-bg-gray-100"
                  onClick={() => requestSort("price")}
                >
                  <div className="ay-flex ay-items-center ay-gap-1">
                    <FiDollarSign className="ay-text-gray-400" />
                    Price {getSortDirectionIndicator("price")}
                  </div>
                </th>
                <th
                  className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider ay-cursor-pointer ay-hover:ay-bg-gray-100"
                  onClick={() => requestSort("tag")}
                >
                  <div className="ay-flex ay-items-center ay-gap-1">
                    <FiTag className="ay-text-gray-400" />
                    Tag {getSortDirectionIndicator("tag")}
                  </div>
                </th>
                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="ay-bg-white ay-divide-y ay-divide-gray-200">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="ay-px-6 ay-py-8 ay-text-center ay-text-gray-500"
                  >
                    {searchTerm
                      ? "No products match your search."
                      : "No products found in this family."}
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr
                    key={product.code}
                    className={`ay-transition-colors ${
                      isEditMode
                        ? "ay-hover:ay-bg-blue-50"
                        : "ay-hover:ay-bg-gray-50"
                    }`}
                  >
                    <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                      <EditableCell product={product} field="name" />
                    </td>
                    <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                      <code className="ay-text-sm ay-font-mono ay-bg-gray-100 ay-px-2 ay-py-1 ay-rounded">
                        {product.code}
                      </code>
                    </td>
                    <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                      <EditableCell product={product} field="onpage_code" />
                    </td>
                    <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                      <EditableCell
                        product={product}
                        field="price"
                        type="number"
                      />
                    </td>
                    <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                      <EditableCell
                        product={product}
                        field="tag"
                        type="select"
                        options={tagOptions}
                      />
                    </td>
                    <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap ay-text-sm ay-font-medium">
                      <div className="ay-flex ay-gap-2">
                        <button
                          onClick={() => onProductView(product.code)}
                          className="ay-p-1.5 ay-bg-blue-500 ay-text-white ay-rounded ay-hover:ay-bg-blue-600"
                          title="View Product"
                          disabled={isEditMode}
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => onProductEdit(product)}
                          className="ay-p-1.5 ay-bg-yellow-500 ay-text-white ay-rounded ay-hover:ay-bg-yellow-600"
                          title="Edit Product"
                          disabled={isEditMode}
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => onProductDelete(product)}
                          className="ay-p-1.5 ay-bg-red-500 ay-text-white ay-rounded ay-hover:ay-bg-red-600"
                          title="Delete Product"
                          disabled={isEditMode}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="ay-px-4 ay-py-3 ay-bg-gray-50 ay-text-right ay-border-t ay-border-gray-200">
          {isEditMode && hasChanges && (
            <div className="ay-text-left ay-text-sm ay-text-orange-600 ay-mb-2">
              ⚠️ You have unsaved changes. Remember to save before closing.
            </div>
          )}
          <button
            onClick={onClose}
            className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
            disabled={saving}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProductsManagerModal
